/*!
 * cobweb-background.js — responsive animated cobweb background
 * Zero dependencies. Canvas 2D. Safe for Shopify Online Store 2.0 themes.
 *
 * Usage (auto):   <canvas data-cobweb-background data-theme="moonlight"></canvas>
 * Usage (manual): CobwebBackground.mount(canvasEl, { theme: 'moonlight' })
 *
 * Derived from cobweb-frame-generator/src/utils/spiderWebGenerator.ts
 * (same PseudoRandom seed 1337, same hub geometry, same draw passes) with
 * responsive profiles, motion/perf guards and a content scrim added.
 */
(function (root) {
  'use strict';

  var THEMES = {
    moonlight: { name: 'Moonlit Silver', bgDark: '#07090e', bgRadial: '#0f141f', glowColor: 'rgba(180, 215, 255, 0.18)', threadPrimary: 'rgba(230, 240, 255, 0.78)', threadSecondary: 'rgba(170, 195, 225, 0.38)', dewColor: 'rgba(245, 250, 255, 0.95)', accentText: '#d4e5f7' },
    noir: { name: 'Gothic Noir', bgDark: '#040406', bgRadial: '#0a0a0e', glowColor: 'rgba(255, 255, 255, 0.12)', threadPrimary: 'rgba(220, 225, 230, 0.72)', threadSecondary: 'rgba(140, 145, 150, 0.32)', dewColor: 'rgba(255, 255, 255, 0.9)', accentText: '#e2e4e8' },
    blood: { name: 'Crimson Eclipse', bgDark: '#0d0406', bgRadial: '#1c080d', glowColor: 'rgba(255, 80, 100, 0.22)', threadPrimary: 'rgba(255, 210, 215, 0.8)', threadSecondary: 'rgba(200, 90, 110, 0.4)', dewColor: 'rgba(255, 230, 235, 0.95)', accentText: '#fca5a5' },
    emerald: { name: 'Spectral Crypt', bgDark: '#040c09', bgRadial: '#091c15', glowColor: 'rgba(90, 240, 180, 0.18)', threadPrimary: 'rgba(215, 255, 240, 0.78)', threadSecondary: 'rgba(100, 190, 150, 0.38)', dewColor: 'rgba(235, 255, 245, 0.95)', accentText: '#a7f3d0' },
    violet: { name: 'Phantom Orchid', bgDark: '#090511', bgRadial: '#160c24', glowColor: 'rgba(190, 110, 255, 0.22)', threadPrimary: 'rgba(240, 220, 255, 0.8)', threadSecondary: 'rgba(170, 120, 220, 0.4)', dewColor: 'rgba(250, 235, 255, 0.95)', accentText: '#e9d5ff' },
    amber: { name: 'Ancient Relic', bgDark: '#0a0805', bgRadial: '#1a140a', glowColor: 'rgba(255, 190, 90, 0.18)', threadPrimary: 'rgba(255, 240, 215, 0.78)', threadSecondary: 'rgba(210, 165, 110, 0.38)', dewColor: 'rgba(255, 248, 230, 0.95)', accentText: '#fde68a' }
  };

  /* ---------- responsive profiles ---------------------------------------- */
  /* Breakpoints deliberately match Dawn's: 750 / 990 / 1400.               */
  var PROFILES = [
    { id: 'mobile',  max: 749,      density: 0.55, hubs: ['tl', 'br'],             edges: ['top', 'bottom'],                 mesh: 0.4, dew: 0.45, pointer: false, dprCap: 1.5, vignette: 0.55, glow: 0.35 },
    { id: 'tablet',  max: 989,      density: 0.7,  hubs: ['tl', 'tr', 'br'],       edges: ['top', 'bottom'],                 mesh: 0.6, dew: 0.6,  pointer: false, dprCap: 2,   vignette: 0.7,  glow: 0.45 },
    { id: 'laptop',  max: 1399,     density: 0.9,  hubs: ['tl', 'bl', 'tr', 'br'], edges: ['top', 'bottom', 'left', 'right'], mesh: 0.85, dew: 0.85, pointer: true,  dprCap: 2,   vignette: 0.9,  glow: 0.5 },
    { id: 'desktop', max: Infinity, density: 1.0,  hubs: ['tl', 'bl', 'tr', 'br'], edges: ['top', 'bottom', 'left', 'right'], mesh: 1.0, dew: 1.0,  pointer: true,  dprCap: 2,   vignette: 1.0,  glow: 0.5 }
  ];

  function profileFor(width) {
    for (var i = 0; i < PROFILES.length; i++) if (width <= PROFILES[i].max) return PROFILES[i];
    return PROFILES[PROFILES.length - 1];
  }

  /* ---------- deterministic rng ------------------------------------------ */
  function Rng(seed) { this.seed = seed || 1337; }
  Rng.prototype.next = function () { this.seed = (this.seed * 9301 + 49297) % 233280; return this.seed / 233280; };
  Rng.prototype.range = function (a, b) { return a + this.next() * (b - a); };

  /* ---------- model ------------------------------------------------------ */
  function buildWebModel(width, height, p, dewEnabled) {
    var rng = new Rng(1337);
    var dewdrops = [];
    var minD = Math.min(width, height);
    var d = p.density;

    var ALL = {
      tl: { hub: { x: width * 0.10, y: height * 0.14 }, a0: -Math.PI * 0.35, a1: Math.PI * 0.9,  n: 28, rMin: minD * 0.42, rMax: minD * 0.65, rings: 24, inner: 8 },
      bl: { hub: { x: width * 0.09, y: height * 0.81 }, a0: -Math.PI * 0.75, a1: Math.PI * 0.38, n: 24, rMin: minD * 0.38, rMax: minD * 0.58, rings: 22, inner: 7 },
      tr: { hub: { x: width * 0.91, y: height * 0.12 }, a0: Math.PI * 0.35,  a1: Math.PI * 1.35, n: 22, rMin: minD * 0.32, rMax: minD * 0.52, rings: 20, inner: 6 },
      br: { hub: { x: width * 0.89, y: height * 0.84 }, a0: Math.PI * 0.65,  a1: Math.PI * 1.7,  n: 26, rMin: minD * 0.40, rMax: minD * 0.62, rings: 23, inner: 7 }
    };

    var orbs = p.hubs.map(function (key) {
      var cfg = ALL[key];
      var numRadials = Math.max(6, Math.round(cfg.n * d));
      var rings = Math.max(5, Math.round(cfg.rings * d));
      var radials = [], spirals = [], frameLines = [];
      var step = (cfg.a1 - cfg.a0) / (numRadials - 1);

      for (var i = 0; i < numRadials; i++) {
        var angle = cfg.a0 + i * step + rng.range(-0.02, 0.02);
        var reach = rng.range(cfg.rMin, cfg.rMax);
        var endX = cfg.hub.x + Math.cos(angle) * reach;
        var endY = cfg.hub.y + Math.sin(angle) * reach;
        var cAng = angle + rng.range(-0.04, 0.04);
        radials.push({ angle: angle, length: reach, endX: endX, endY: endY,
          cc: { x: cfg.hub.x + Math.cos(cAng) * reach * 0.5, y: cfg.hub.y + Math.sin(cAng) * reach * 0.5 } });

        if (dewEnabled && rng.next() > (1 - 0.6 * p.dew)) {
          var count = Math.floor(rng.range(2, 6) * p.dew);
          for (var k = 0; k < count; k++) {
            var t = rng.range(0.2, 0.95);
            dewdrops.push({ x: cfg.hub.x + (endX - cfg.hub.x) * t, y: cfg.hub.y + (endY - cfg.hub.y) * t,
              r: rng.range(0.8, 1.8), a: rng.range(0.6, 0.95), ph: rng.range(0, Math.PI * 2) });
          }
        }
      }

      for (var f = 0; f < radials.length - 1; f++) {
        var r1 = radials[f], r2 = radials[f + 1];
        var mx = (r1.endX + r2.endX) / 2, my = (r1.endY + r2.endY) / 2;
        frameLines.push({ p1: { x: r1.endX, y: r1.endY }, p2: { x: r2.endX, y: r2.endY },
          c: { x: mx + (cfg.hub.x - mx) * 0.15, y: my + (cfg.hub.y - my) * 0.15 },
          w: rng.range(0.9, 1.4), alpha: rng.range(0.5, 0.8) });
      }

      for (var ring = 0; ring < rings; ring++) {
        var base = cfg.inner + Math.pow(ring / rings, 1.15) * (cfg.rMax * 0.82);
        for (var s = 0; s < radials.length - 1; s++) {
          if (ring > rings * 0.7 && rng.next() < 0.12) continue;
          var ra = radials[s], rb = radials[s + 1];
          var d1 = Math.min(ra.length * 0.95, base + rng.range(-2, 2));
          var d2 = Math.min(rb.length * 0.95, base + rng.range(-2, 2));
          var p1 = { x: cfg.hub.x + Math.cos(ra.angle) * d1, y: cfg.hub.y + Math.sin(ra.angle) * d1 };
          var p2 = { x: cfg.hub.x + Math.cos(rb.angle) * d2, y: cfg.hub.y + Math.sin(rb.angle) * d2 };
          var sag = Math.max(1, (d1 + d2) * 0.04);
          var mid = (d1 + d2) / 2, midA = (ra.angle + rb.angle) / 2;
          var sp = { x: cfg.hub.x + Math.cos(midA) * (mid - sag), y: cfg.hub.y + Math.sin(midA) * (mid - sag) };
          spirals.push({ p1: p1, p2: p2 });

          if (dewEnabled && rng.next() > (1 - 0.65 * p.dew)) {
            var tt = rng.range(0.2, 0.8);
            dewdrops.push({
              x: (1 - tt) * (1 - tt) * p1.x + 2 * (1 - tt) * tt * sp.x + tt * tt * p2.x,
              y: (1 - tt) * (1 - tt) * p1.y + 2 * (1 - tt) * tt * sp.y + tt * tt * p2.y,
              r: rng.range(0.7, 1.6), a: rng.range(0.55, 0.95), ph: rng.range(0, Math.PI * 2) });
          }
        }
      }
      return { hub: cfg.hub, radials: radials, spirals: spirals, frameLines: frameLines };
    });

    var cables = [];
    function pushCable(p1, p2, cp1, w, a) { cables.push({ p1: p1, p2: p2, cp1: cp1, w: w, alpha: a }); }

    if (p.edges.indexOf('top') > -1) {
      var nt = Math.max(3, Math.round(9 * d));
      for (var i1 = 0; i1 < nt; i1++) pushCable(
        { x: width * (0.08 + (i1 / nt) * 0.12), y: height * rng.range(0.04, 0.14) },
        { x: width * (0.92 - (i1 / nt) * 0.12), y: height * rng.range(0.04, 0.14) },
        { x: width * 0.5, y: height * (0.03 + (i1 / nt) * 0.11) }, rng.range(0.8, 1.8), rng.range(0.45, 0.85));
    }
    if (p.edges.indexOf('bottom') > -1) {
      var nb = Math.max(3, Math.round(11 * d));
      for (var i2 = 0; i2 < nb; i2++) pushCable(
        { x: width * (0.06 + (i2 / nb) * 0.16), y: height * rng.range(0.78, 0.94) },
        { x: width * (0.94 - (i2 / nb) * 0.16), y: height * rng.range(0.78, 0.94) },
        { x: width * 0.5, y: height * (0.97 - (i2 / nb) * 0.12) }, rng.range(0.8, 1.9), rng.range(0.45, 0.85));
    }
    if (p.edges.indexOf('left') > -1) {
      var nl = Math.max(3, Math.round(8 * d));
      for (var i3 = 0; i3 < nl; i3++) pushCable(
        { x: width * rng.range(0.04, 0.12), y: height * (0.16 + (i3 / nl) * 0.15) },
        { x: width * rng.range(0.04, 0.12), y: height * (0.84 - (i3 / nl) * 0.15) },
        { x: width * (0.08 + (i3 / nl) * 0.08), y: height * 0.5 }, rng.range(0.7, 1.5), rng.range(0.4, 0.8));
    }
    if (p.edges.indexOf('right') > -1) {
      var nr = Math.max(3, Math.round(8 * d));
      for (var i4 = 0; i4 < nr; i4++) pushCable(
        { x: width * rng.range(0.88, 0.96), y: height * (0.14 + (i4 / nr) * 0.15) },
        { x: width * rng.range(0.88, 0.96), y: height * (0.86 - (i4 / nr) * 0.15) },
        { x: width * (0.92 - (i4 / nr) * 0.08), y: height * 0.5 }, rng.range(0.7, 1.5), rng.range(0.4, 0.8));
    }

    var mesh = [];
    var meshCount = Math.round(80 * d * p.mesh);
    for (var m = 0; m < meshCount; m++) {
      var region = rng.next(), x, y;
      if (region < 0.35) { x = width * rng.range(0.12, 0.88);
        mesh.push({ p1: { x: x + rng.range(-20, 20), y: height * rng.range(0.01, 0.08) }, p2: { x: x + rng.range(-20, 20), y: height * rng.range(0.06, 0.16) }, alpha: rng.range(0.15, 0.45), w: rng.range(0.4, 0.8) });
      } else if (region < 0.70) { x = width * rng.range(0.12, 0.88);
        mesh.push({ p1: { x: x + rng.range(-20, 20), y: height * rng.range(0.84, 0.94) }, p2: { x: x + rng.range(-20, 20), y: height * rng.range(0.92, 0.99) }, alpha: rng.range(0.15, 0.45), w: rng.range(0.4, 0.8) });
      } else if (region < 0.85) { y = height * rng.range(0.25, 0.75);
        mesh.push({ p1: { x: width * rng.range(0.01, 0.06), y: y + rng.range(-15, 15) }, p2: { x: width * rng.range(0.05, 0.14), y: y + rng.range(-15, 15) }, alpha: rng.range(0.15, 0.4), w: rng.range(0.4, 0.8) });
      } else { y = height * rng.range(0.25, 0.75);
        mesh.push({ p1: { x: width * rng.range(0.94, 0.99), y: y + rng.range(-15, 15) }, p2: { x: width * rng.range(0.86, 0.95), y: y + rng.range(-15, 15) }, alpha: rng.range(0.15, 0.4), w: rng.range(0.4, 0.8) });
      }
    }

    if (dewEnabled) {
      cables.forEach(function (c) {
        var n = Math.floor(rng.range(3, 10) * p.dew);
        for (var i = 0; i < n; i++) {
          var t = rng.range(0.05, 0.95);
          dewdrops.push({
            x: (1 - t) * (1 - t) * c.p1.x + 2 * (1 - t) * t * c.cp1.x + t * t * c.p2.x,
            y: (1 - t) * (1 - t) * c.p1.y + 2 * (1 - t) * t * c.cp1.y + t * t * c.p2.y,
            r: rng.range(0.8, 2.0), a: rng.range(0.65, 0.98), ph: rng.range(0, Math.PI * 2) });
        }
      });
    }

    return { width: width, height: height, orbs: orbs, cables: cables, mesh: mesh, dewdrops: dewdrops };
  }

  /* ---------- draw -------------------------------------------------------- */
  function draw(ctx, model, theme, time, mouse, glow, dewOn, vignette, scrim) {
    var w = model.width, h = model.height;
    ctx.clearRect(0, 0, w, h);

    var bg = ctx.createRadialGradient(w * 0.5, h * 0.5, Math.min(w, h) * 0.15, w * 0.5, h * 0.5, Math.max(w, h) * 0.72);
    bg.addColorStop(0, theme.bgDark);
    bg.addColorStop(0.6, theme.bgRadial);
    bg.addColorStop(1, '#020305');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    model.orbs.forEach(function (orb) {
      var rad = Math.min(w, h) * (0.35 + glow * 0.2);
      var g = ctx.createRadialGradient(orb.hub.x, orb.hub.y, 10, orb.hub.x, orb.hub.y, rad);
      g.addColorStop(0, theme.glowColor);
      g.addColorStop(0.5, theme.glowColor.replace(/[\d.]+\)$/, '0.06)'));
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
    });

    function vib(p) {
      if (!mouse) return 0;
      var dx = p.x - mouse.x, dy = p.y - mouse.y;
      var dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 180) return Math.sin(time * 14 + dist * 0.1) * (1 - dist / 180) * 3.0;
      return 0;
    }

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.save();
    ctx.strokeStyle = theme.threadSecondary;
    model.mesh.forEach(function (m) {
      ctx.lineWidth = m.w; ctx.globalAlpha = m.alpha * 0.85;
      ctx.beginPath(); ctx.moveTo(m.p1.x, m.p1.y); ctx.lineTo(m.p2.x, m.p2.y); ctx.stroke();
    });
    ctx.restore();

    ctx.save();
    model.cables.forEach(function (c) {
      var v1 = vib(c.p1), vm = vib(c.cp1), v2 = vib(c.p2);
      ctx.strokeStyle = theme.threadPrimary; ctx.lineWidth = c.w; ctx.globalAlpha = c.alpha;
      ctx.beginPath();
      ctx.moveTo(c.p1.x + v1, c.p1.y + v1);
      ctx.quadraticCurveTo(c.cp1.x + vm, c.cp1.y + vm, c.p2.x + v2, c.p2.y + v2);
      ctx.stroke();
      if (glow > 0.3 && c.w > 1.2) {
        ctx.save();
        ctx.strokeStyle = theme.dewColor; ctx.lineWidth = c.w * 0.4; ctx.globalAlpha = c.alpha * 0.5;
        ctx.stroke();
        ctx.restore();
      }
    });
    ctx.restore();

    model.orbs.forEach(function (orb) {
      ctx.save();
      ctx.strokeStyle = theme.threadPrimary;
      orb.frameLines.forEach(function (f) {
        ctx.lineWidth = f.w; ctx.globalAlpha = f.alpha;
        ctx.beginPath(); ctx.moveTo(f.p1.x, f.p1.y);
        ctx.quadraticCurveTo(f.c.x, f.c.y, f.p2.x, f.p2.y); ctx.stroke();
      });
      ctx.restore();

      ctx.save();
      ctx.strokeStyle = theme.threadPrimary;
      ctx.lineWidth = 0.9; ctx.globalAlpha = 0.65;
      orb.radials.forEach(function (r) {
        var v = vib({ x: r.endX, y: r.endY });
        ctx.beginPath(); ctx.moveTo(orb.hub.x, orb.hub.y);
        ctx.quadraticCurveTo(r.cc.x + v * 0.5, r.cc.y + v * 0.5, r.endX + v, r.endY + v);
        ctx.stroke();
      });
      ctx.restore();

      ctx.save();
      ctx.strokeStyle = theme.threadSecondary;
      ctx.lineWidth = 0.75; ctx.globalAlpha = 0.55;
      orb.spirals.forEach(function (s) {
        var v1 = vib(s.p1), v2 = vib(s.p2);
        var mx = (s.p1.x + s.p2.x) / 2, my = (s.p1.y + s.p2.y) / 2;
        ctx.beginPath();
        ctx.moveTo(s.p1.x + v1, s.p1.y + v1);
        ctx.quadraticCurveTo(mx + (orb.hub.x - mx) * 0.12 + (v1 + v2) * 0.5, my + (orb.hub.y - my) * 0.12 + (v1 + v2) * 0.5, s.p2.x + v2, s.p2.y + v2);
        ctx.stroke();
      });
      ctx.restore();

      ctx.save();
      ctx.fillStyle = theme.threadPrimary; ctx.globalAlpha = 0.8;
      ctx.beginPath(); ctx.arc(orb.hub.x, orb.hub.y, 2.5, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    });

    if (dewOn) {
      ctx.save();
      model.dewdrops.forEach(function (dp) {
        var shimmer = Math.sin(time * 2.2 + dp.ph) * 0.25 + 0.75;
        var alpha = dp.a * shimmer;
        if (alpha <= 0.05) return;
        ctx.fillStyle = theme.dewColor; ctx.globalAlpha = alpha;
        ctx.beginPath(); ctx.arc(dp.x, dp.y, dp.r, 0, Math.PI * 2); ctx.fill();
        if (dp.r > 1.2 && glow > 0.25) {
          ctx.save();
          ctx.fillStyle = theme.glowColor; ctx.globalAlpha = alpha * 0.45;
          ctx.beginPath(); ctx.arc(dp.x, dp.y, dp.r * 2.4, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
        }
      });
      ctx.restore();
    }

    if (vignette > 0) {
      var v = ctx.createRadialGradient(w * 0.5, h * 0.5, Math.min(w, h) * 0.48, w * 0.5, h * 0.5, Math.max(w, h) * 0.75);
      v.addColorStop(0, 'rgba(0,0,0,0)');
      v.addColorStop(0.7, 'rgba(0,0,0,' + (0.3 * vignette).toFixed(3) + ')');
      v.addColorStop(1, 'rgba(0,0,0,' + (0.85 * vignette).toFixed(3) + ')');
      ctx.fillStyle = v;
      ctx.fillRect(0, 0, w, h);
    }

    /* Content scrim — keeps product copy at AA contrast over the web. */
    if (scrim > 0) {
      var s2 = ctx.createRadialGradient(w * 0.5, h * 0.45, 0, w * 0.5, h * 0.45, Math.max(w, h) * 0.55);
      s2.addColorStop(0, 'rgba(3,4,7,' + (0.72 * scrim).toFixed(3) + ')');
      s2.addColorStop(0.55, 'rgba(3,4,7,' + (0.45 * scrim).toFixed(3) + ')');
      s2.addColorStop(1, 'rgba(3,4,7,0)');
      ctx.fillStyle = s2;
      ctx.fillRect(0, 0, w, h);
    }
  }

  /* ---------- mount ------------------------------------------------------ */
  function mount(canvas, options) {
    var o = options || {};
    var theme = THEMES[o.theme] || THEMES.moonlight;
    var reduced = false;
    try { reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
    if (o.motion === 'off') reduced = true;

    var host = o.host || canvas.parentElement || canvas;
    var ctx = canvas.getContext('2d', { alpha: false });
    var model = null, profile = null, raf = null, mouse = null, visible = true, resizeTimer = null;

    function sizeAndBuild() {
      /* o.measure lets a scaled preview report its LOGICAL size (e.g. 1440x900)
         instead of its post-transform box. Production never passes it. */
      var rect = o.measure ? o.measure() : host.getBoundingClientRect();
      var w = Math.floor(rect.width), h = Math.floor(rect.height);
      if (w <= 0 || h <= 0) return;
      profile = profileFor(w);
      var dpr = Math.min(window.devicePixelRatio || 1, profile.dprCap);
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      model = buildWebModel(w, h, profile, o.dewdrops !== false);
      canvas.setAttribute('data-profile', profile.id);
      if (reduced) paint(0);
    }

    function paint(time) {
      if (!model) return;
      draw(ctx, model, theme,
        time,
        (profile.pointer && o.pointer !== false) ? mouse : null,
        o.glow != null ? o.glow : profile.glow,
        o.dewdrops !== false,
        o.vignette != null ? o.vignette : profile.vignette,
        o.scrim != null ? o.scrim : 0.9);
    }

    function frame(t) { paint(t * 0.001); raf = requestAnimationFrame(frame); }
    function start() { if (!reduced && raf == null) raf = requestAnimationFrame(frame); }
    function stop() { if (raf != null) { cancelAnimationFrame(raf); raf = null; } }

    var onResize = function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(sizeAndBuild, 150);
    };
    var onMove = function (e) {
      var r = host.getBoundingClientRect();
      mouse = { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    var onLeave = function () { mouse = null; };
    var onVis = function () { if (document.hidden || !visible) stop(); else start(); };

    sizeAndBuild();
    if (window.ResizeObserver) { var ro = new ResizeObserver(onResize); ro.observe(host); }
    else window.addEventListener('resize', onResize);
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerleave', onLeave);
    document.addEventListener('visibilitychange', onVis);

    var io = null;
    if (window.IntersectionObserver) {
      io = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        onVis();
      }, { threshold: 0 });
      io.observe(canvas);
    }
    start();

    return {
      destroy: function () {
        stop();
        if (ro) ro.disconnect(); else window.removeEventListener('resize', onResize);
        if (io) io.disconnect();
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerleave', onLeave);
        document.removeEventListener('visibilitychange', onVis);
      },
      setTheme: function (id) { theme = THEMES[id] || theme; if (reduced) paint(0); },
      profile: function () { return profile && profile.id; }
    };
  }

  function autoInit() {
    var nodes = document.querySelectorAll('[data-cobweb-background]:not([data-cobweb-ready])');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      el.setAttribute('data-cobweb-ready', '');
      mount(el, {
        theme: el.getAttribute('data-theme') || 'moonlight',
        dewdrops: el.getAttribute('data-dewdrops') !== 'false',
        pointer: el.getAttribute('data-pointer') !== 'false',
        motion: el.getAttribute('data-motion') || 'auto',
        scrim: el.hasAttribute('data-scrim') ? parseFloat(el.getAttribute('data-scrim')) : 0.9
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoInit);
  else autoInit();

  root.CobwebBackground = { mount: mount, THEMES: THEMES, PROFILES: PROFILES, autoInit: autoInit, buildWebModel: buildWebModel, draw: draw };
})(window);
